import { db } from '../firebase';
import { collection, doc, setDoc, getDocs, getDoc, updateDoc, arrayUnion, arrayRemove, writeBatch } from 'firebase/firestore';
import { nanoid } from 'nanoid';
import { userProfileService } from './userProfileService';
import { GROUP_TYPES, computeFeaturePermissions, isExternalGroupSet } from '../schemas/defaultGroups';
import { setupDefaultGroupAccess } from '../utils/accessControl';
import { multiFamilyService } from './multiFamilyService';
import logger from '../utils/logger';

// Maps a relationship string to the appropriate group type keys.
// Called during joinFamily when autoAssignGroups is not explicitly provided.
const RELATIONSHIP_TO_GROUPS = {
  // Immediate family
  spouse: ['IMMEDIATE_FAMILY'],
  partner: ['IMMEDIATE_FAMILY'],
  husband: ['IMMEDIATE_FAMILY'],
  wife: ['IMMEDIATE_FAMILY'],
  sibling: ['IMMEDIATE_FAMILY'],
  brother: ['IMMEDIATE_FAMILY'],
  sister: ['IMMEDIATE_FAMILY'],
  // Parents/Guardians
  parent: ['PARENTS', 'IMMEDIATE_FAMILY'],
  father: ['PARENTS', 'IMMEDIATE_FAMILY'],
  mother: ['PARENTS', 'IMMEDIATE_FAMILY'],
  guardian: ['PARENTS', 'IMMEDIATE_FAMILY'],
  // Children/Minors
  child: ['CHILDREN_MINORS'],
  son: ['CHILDREN_MINORS'],
  daughter: ['CHILDREN_MINORS'],
  minor: ['CHILDREN_MINORS'],
  // Grandparents
  grandparent: ['GRANDPARENTS'],
  grandfather: ['GRANDPARENTS'],
  grandmother: ['GRANDPARENTS'],
  // Grandchildren
  grandchild: ['SIBLING_CHILDREN'],
  grandson: ['SIBLING_CHILDREN'],
  granddaughter: ['SIBLING_CHILDREN'],
  // Extended family
  aunt: ['PARENTS_SIBLINGS'],
  uncle: ['PARENTS_SIBLINGS'],
  cousin: ['SIBLING_CHILDREN'],
  niece: ['SIBLING_CHILDREN'],
  nephew: ['SIBLING_CHILDREN'],
  // Distant / Friends
  'extended family': ['EXTENDED_FAMILY'],
  friend: ['FRIENDS_OF_FAMILY'],
  caregiver: ['CAREGIVERS'],
};

export const getGroupsForRelationship = (relationship) => {
  if (!relationship) return ['IMMEDIATE_FAMILY'];
  const key = relationship.toLowerCase().trim();
  return RELATIONSHIP_TO_GROUPS[key] || ['IMMEDIATE_FAMILY'];
};

/**
 * Service for managing family codes
 */
class FamilyCodeService {
  constructor() {
    this.familiesCollection = collection(db, 'families');
    this.usersCollection = collection(db, 'users');
  }

  /**
   * Initialize default groups for a new family (NESTED STRUCTURE)
   * @param {string} familyCode - Family code
   * @param {string} ownerId - Owner's user ID
   * @private
   */
  async initializeDefaultGroups(familyCode, ownerId) {
    const batch = writeBatch(db);

    try {
      const familyRef = doc(this.familiesCollection, familyCode);

      // Owner gets ADMIN + IMMEDIATE_FAMILY groups
      const ownerGroups = ['IMMEDIATE_FAMILY', 'ADMIN'];

      // Create all 10 default groups in nested structure
      for (const [type, groupType] of Object.entries(GROUP_TYPES)) {
        const shouldAddOwner = ownerGroups.includes(type);

        // Create group in families/{code}/groups/{type}/
        const groupRef = doc(familyRef, 'groups', type);
        const groupData = {
          name: groupType.name,
          type: type,
          description: groupType.description,
          permissions: groupType.permissions,
          familyCode: familyCode,
          members: shouldAddOwner ? [ownerId] : [],
          admins: shouldAddOwner ? [ownerId] : [],
          createdBy: ownerId,
          createdAt: new Date(),
          updatedAt: new Date()
        };

        batch.set(groupRef, groupData);
      }

      await batch.commit();
      return true;
    } catch (error) {
      logger.error('Error in initializeDefaultGroups:', error);
      throw error;
    }
  }

  /**
   * Generate a unique family code based on family name or generate a random one
   * @param {string} [familyName] - Optional family name to base code on
   * @returns {Promise<string>} Generated unique family code
   */
  async generateFamilyCode(familyName) {
    // If no family name provided, use legacy random code generation
    if (!familyName) {
      const code = nanoid(8).toUpperCase();
      const existingCode = await this.getFamilyByCode(code);
      if (existingCode) {
        return this.generateFamilyCode();
      }
      return code;
    }

    // New family name based generation
    const cleanName = familyName.toUpperCase().replace(/[^A-Z]/g, '');
    const baseCode = cleanName.slice(0, 4);
    let suffix = 1;
    let familyCode;
    
    do {
      familyCode = `${baseCode}${suffix.toString().padStart(4, '0')}`;
      const existingFamily = await getDoc(doc(this.familiesCollection, familyCode));
      if (!existingFamily.exists()) {
        return familyCode;
      }
      suffix++;
    } while (suffix < 10000);
    
    // Fall back to legacy generation if no unique code found
    return this.generateFamilyCode();
  }

  /**
   * Get family by code
   * @param {string} code - Family code to look up
   * @returns {Promise<Object|null>} Family data or null if not found
   */
  async getFamilyByCode(code) {
    const familyRef = doc(this.familiesCollection, code);
    const familyDoc = await getDoc(familyRef);
    
    if (!familyDoc.exists()) {
      return null;
    }
    
    return {
      id: familyDoc.id,
      ...familyDoc.data()
    };
  }

  /**
   * Create a new family (NESTED STRUCTURE)
   * @param {string} userId - ID of user creating the family
   * @param {string} [familyName] - Optional family name
   * @returns {Promise<string>} Created family code
   */
  async createFamily(userId, familyName) {

    // Get user data
    const userDoc = await getDoc(doc(this.usersCollection, userId));
    if (!userDoc.exists()) {
      logger.error('User not found:', userId);
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const familyCode = await this.generateFamilyCode(familyName || userData?.lastName);

    try {
      const batch = writeBatch(db);
      const familyRef = doc(this.familiesCollection, familyCode);

      // Create the family document
      const familyData = {
        name: familyName ? `${familyName} Family` : `Family ${familyCode}`,
        familyCode: familyCode, // Changed from 'code' to 'familyCode' for consistency
        ownerId: userId,
        createdBy: userId, // Required by Firestore security rules for family creation
        ownerEmail: userData.email,
        memberCount: 1,
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true
      };

      batch.set(familyRef, familyData);

      // Create nested member document for owner
      const memberRef = doc(familyRef, 'members', userId);
      const memberData = {
        uid: userId,
        email: userData.email,
        displayName: userData.displayName || `${userData.firstName} ${userData.lastName}`,
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone || null,
        role: 'owner',
        groups: ['ADMIN', 'IMMEDIATE_FAMILY'], // ✅ Groups array populated!
        isActive: true,
        isVerified: userData.isVerified || true,
        accountStatus: userData.accountStatus || 'active',
        joinedAt: new Date(),
        updatedAt: new Date(),
        notificationPreferences: userData.notificationPreferences || {
          push: true,
          email: true,
          sms: false,
          emailFrequency: 'instant'
        }
      };

      batch.set(memberRef, memberData);

      // Update top-level user document with ownership fields
      // CRITICAL: Firestore rules check getUserData() from the top-level user doc,
      // so role, groups, and isAdmin MUST be set here for permissions to work
      const userRef = doc(this.usersCollection, userId);
      const userUpdateData = {
        activeFamilyCode: familyCode,
        currentFamily: familyCode, // Legacy compat
        familyCode: familyCode, // Legacy compat
        families: arrayUnion(familyCode), // Legacy compat
        role: 'owner',
        groups: ['ADMIN', 'IMMEDIATE_FAMILY'],
        isAdmin: true,
        isExternal: false,
        updatedAt: new Date()
      };

      batch.update(userRef, userUpdateData);

      await batch.commit();

      // Initialize default groups AFTER main batch commits
      // This ensures the user doc has familyCode set for hasValidFamilyCode() rule checks
      await this.initializeDefaultGroups(familyCode, userId);

      // Track this family in the user's multi-family list
      await multiFamilyService.addFamilyToUser(userId, familyCode);

      return familyCode;
    } catch (error) {
      logger.error('Error in createFamily:', error);
      throw error;
    }
  }

  /**
   * Validate a family code
   * @param {string} code - Family code to validate
   * @returns {Promise<boolean>} Whether code is valid
   */
  async validateFamilyCode(code) {
    if (!code || typeof code !== 'string') {
      return false;
    }
    
    const family = await this.getFamilyByCode(code);
    return family !== null && family.isActive;
  }

  /**
   * Get family members (NESTED STRUCTURE)
   * @param {string} familyCode - Family code to get members for
   * @returns {Promise<Array>} Array of family members
   */
  async getFamilyMembers(familyCode) {
    const familyRef = doc(this.familiesCollection, familyCode);
    const membersCollection = collection(familyRef, 'members');
    const querySnapshot = await getDocs(membersCollection);

    const members = [];

    querySnapshot.forEach(doc => {
      members.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return members;
  }

  /**
   * Update member role
   * @param {string} familyCode - Family code
   * @param {string} memberId - Member ID to update
   * @param {string} newRole - New role to assign
   */
  async updateMemberRole(familyCode, memberId, newRole) {
    await userProfileService.updateUserProfile(memberId, {
      role: newRole,
      updatedAt: new Date()
    });
  }

  /**
   * Remove family member
   * @param {string} familyCode - Family code
   * @param {string} memberId - Member ID to remove
   */
  async removeFamilyMember(familyCode, memberId) {
    await userProfileService.updateUserProfile(memberId, {
      familyCode: null,
      role: 'member',
      isMainContact: false,
      updatedAt: new Date()
    });
  }

  /**
   * Join an existing family (NESTED STRUCTURE)
   * @param {string} userId - User ID joining the family
   * @param {string} familyCode - Family code to join
   * @param {Object} options - Join options
   * @param {string} options.relationship - Relationship to inviting member
   * @param {Array} options.autoAssignGroups - Groups to auto-assign
   * @param {string} options.invitedBy - ID of user who sent the invite
   * @returns {Promise<void>}
   */
  async joinFamily(userId, familyCode, options = {}) {
    // Validate family code
    const familyRef = doc(this.familiesCollection, familyCode);
    const familyDoc = await getDoc(familyRef);
    if (!familyDoc.exists()) {
      throw new Error('Invalid family code');
    }

    // Get user data
    const userDoc = await getDoc(doc(this.usersCollection, userId));
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();

    // Auto-assign groups based on relationship when not explicitly provided
    const assignedGroups = options.autoAssignGroups
      || getGroupsForRelationship(options.relationship);

    // Compute denormalized feature permissions and external flag so Firestore
    // rules can check access without cross-collection reads.
    const featurePermissions = computeFeaturePermissions(assignedGroups);
    const isExternal = isExternalGroupSet(assignedGroups);

    // STAGE 1: Update user document with familyCode and groups FIRST
    // This must happen before memberCount update so Firestore rules can verify
    // the user has familyCode when checking hasValidFamilyCode() during memberCount increment
    // CRITICAL: groups and role must be set on top-level user doc for Firestore rules
    const userRef = doc(this.usersCollection, userId);
    try {
      await updateDoc(userRef, {
        activeFamilyCode: familyCode,
        currentFamily: familyCode, // Legacy compat
        familyCode: familyCode, // Legacy compat
        families: arrayUnion(familyCode), // Legacy compat
        role: 'member',
        groups: assignedGroups,
        featurePermissions,  // Denormalized for Firestore rules
        isExternal,          // True when all groups are professional/external
        isAdmin: false,
        updatedAt: new Date()
      });
    } catch (err) {
      logger.error('joinFamily: Stage 1 user doc update failed:', err);
      throw err;
    }

    // STAGE 2: Batch all other operations
    // Now that user has familyCode, rules will allow memberCount update
    const batch = writeBatch(db);

    // Create nested member document
    const memberRef = doc(familyRef, 'members', userId);
    const memberData = {
      uid: userId,
      email: userData.email,
      displayName: userData.displayName || `${userData.firstName} ${userData.lastName}`,
      firstName: userData.firstName,
      lastName: userData.lastName,
      phone: userData.phone || null,
      role: 'member',
      groups: assignedGroups,
      relationship: options.relationship,
      invitedBy: options.invitedBy,
      isActive: true,
      isVerified: userData.isVerified || true,
      accountStatus: userData.accountStatus || 'active',
      joinedAt: new Date(),
      updatedAt: new Date(),
      notificationPreferences: userData.notificationPreferences || {
        push: true,
        email: true,
        sms: false,
        emailFrequency: 'instant'
      }
    };

    batch.set(memberRef, memberData);

    // Add to appropriate family groups
    for (const groupType of assignedGroups) {
      const groupRef = doc(familyRef, 'groups', groupType);
      const groupDoc = await getDoc(groupRef);

      if (groupDoc.exists()) {
        batch.update(groupRef, {
          members: arrayUnion(userId),
          updatedAt: new Date()
        });
      }
    }

    // Update family member count
    batch.update(familyRef, {
      memberCount: (familyDoc.data().memberCount || 0) + 1,
      updatedAt: new Date()
    });

    try {
      await batch.commit();
    } catch (err) {
      logger.error('joinFamily: Stage 2 batch failed (member doc + groups + memberCount):', err);
      throw err;
    }

    // Populate familyMemberships[] so multi-family switcher picks this up
    try {
      await multiFamilyService.addFamilyToUser(userId, familyCode, 'member');
    } catch (err) {
      logger.error('joinFamily: addFamilyToUser failed:', err);
      throw err;
    }
  }

  /**
   * Create a family invitation
   * @param {string} inviterId - ID of user creating the invitation
   * @param {string} familyCode - Family code
   * @param {string} relationship - Relationship to the inviter
   * @returns {Promise<string>} Invitation code
   */
  async createFamilyInvitation(inviterId, familyCode, relationship) {
    if (!inviterId || !familyCode || !relationship) {
      throw new Error('Inviter ID, family code, and relationship are required');
    }

    // Validate family code
    const family = await this.getFamilyByCode(familyCode);
    if (!family) {
      throw new Error('Invalid family code');
    }

    // Generate invitation code
    const invitationCode = nanoid(12).toUpperCase();
    
    // Create invitation document
    await setDoc(doc(this.familiesCollection, familyCode, 'invitations', invitationCode), {
      inviterId,
      relationship,
      createdAt: new Date(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days expiry
      status: 'pending'
    });

    return invitationCode;
  }

  /**
   * Leave a family (NESTED STRUCTURE)
   * Removes member from family but keeps the user account
   * @param {string} userId - User ID leaving the family
   * @param {string} familyCode - Optional family code (will be fetched from user if not provided)
   * @returns {Promise<void>}
   */
  async leaveFamily(userId, familyCode = null) {
    // Get user data to find familyCode if not provided
    const userDoc = await getDoc(doc(this.usersCollection, userId));
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const targetFamilyCode = familyCode || userData.familyCode || userData.currentFamily;

    if (!targetFamilyCode) {
      throw new Error('User is not a member of any family');
    }

    // Check if user is the family owner
    const familyRef = doc(this.familiesCollection, targetFamilyCode);
    const familyDoc = await getDoc(familyRef);

    if (!familyDoc.exists()) {
      throw new Error('Family not found');
    }

    const familyData = familyDoc.data();
    if (familyData.ownerId === userId) {
      throw new Error('Family owner cannot leave. Please transfer ownership first or delete the family.');
    }

    const batch = writeBatch(db);

    // Remove from nested member document
    const memberRef = doc(familyRef, 'members', userId);
    const memberDoc = await getDoc(memberRef);

    if (memberDoc.exists()) {
      batch.delete(memberRef);
    }

    // Remove from all groups
    const groupsCollection = collection(familyRef, 'groups');
    const groupsSnapshot = await getDocs(groupsCollection);

    for (const groupDoc of groupsSnapshot.docs) {
      const groupData = groupDoc.data();
      if (groupData.members && groupData.members.includes(userId)) {
        batch.update(groupDoc.ref, {
          members: arrayRemove(userId),
          admins: arrayRemove(userId),
          updatedAt: new Date()
        });
      }
    }

    // Update family member count
    const currentCount = familyData.memberCount || 1;
    batch.update(familyRef, {
      memberCount: Math.max(0, currentCount - 1),
      updatedAt: new Date()
    });

    // Remove family from user's families list and clear if it was current
    const userRef = doc(this.usersCollection, userId);
    batch.update(userRef, {
      families: arrayRemove(targetFamilyCode),
      updatedAt: new Date()
    });

    await batch.commit();

    // Switch to another family if available, or clear currentFamily
    await multiFamilyService.removeFamilyFromUser(userId, targetFamilyCode);

    // Notify family admins that member left
    await this.notifyAdminsOfMemberDeparture(userId, targetFamilyCode, memberDoc.exists() ? memberDoc.data() : userData);
  }

  /**
   * Notify family admins when a member leaves
   * @private
   */
  async notifyAdminsOfMemberDeparture(userId, familyCode, memberData) {
    try {
      const familyRef = doc(this.familiesCollection, familyCode);
      const membersCollection = collection(familyRef, 'members');
      const membersSnapshot = await getDocs(membersCollection);

      for (const memberDoc of membersSnapshot.docs) {
        const member = memberDoc.data();
        if (member.groups?.includes('ADMIN') && memberDoc.id !== userId) {
          // Create notification for admin
          const notificationRef = doc(collection(db, 'notifications'));
          await setDoc(notificationRef, {
            recipientId: memberDoc.id,
            type: 'member_left',
            title: 'Member Left Family',
            message: `${memberData.displayName || memberData.email || 'A member'} has left the family.`,
            relatedUserId: userId,
            familyCode,
            read: false,
            createdAt: new Date()
          });
        }
      }
    } catch (err) {
      logger.error('Error notifying admins of member departure:', err);
      // Don't throw - notifications are best-effort
    }
  }

  /**
   * Accept a family invitation
   * @param {string} userId - ID of user accepting the invitation
   * @param {string} invitationCode - Invitation code
   * @returns {Promise<Object>} Family and relationship info
   */
  async acceptFamilyInvitation(userId, invitationCode) {
    // Find invitation across all families
    const families = await getDocs(this.familiesCollection);
    let invitation = null;
    let familyCode = null;

    for (const family of families.docs) {
      const invitationDoc = await getDoc(doc(this.familiesCollection, family.id, 'invitations', invitationCode));
      if (invitationDoc.exists()) {
        invitation = invitationDoc.data();
        familyCode = family.id;
        break;
      }
    }

    if (!invitation) {
      throw new Error('Invalid invitation code');
    }

    if (invitation.status !== 'pending') {
      throw new Error('Invitation is no longer valid');
    }

    if (invitation.expiresAt.toDate() < new Date()) {
      throw new Error('Invitation has expired');
    }

    // Update invitation status
    await updateDoc(doc(this.familiesCollection, familyCode, 'invitations', invitationCode), {
      status: 'accepted',
      acceptedBy: userId,
      acceptedAt: new Date()
    });

    // Update user profile with family code and relationship
    await userProfileService.updateUserProfile(userId, {
      familyCode,
      relationship: invitation.relationship,
      invitedBy: invitation.inviterId,
      updatedAt: new Date()
    });

    // Set up appropriate group access based on relationship
    await setupDefaultGroupAccess(userId, familyCode, invitation.relationship);

    return {
      familyCode,
      relationship: invitation.relationship,
      inviterId: invitation.inviterId
    };
  }
}

export const familyCodeService = new FamilyCodeService(); 